import { Component, effect, inject } from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { AppUtilityService } from '../../../utility/app.utility';
import { PaginatorModule } from 'primeng/paginator';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { ActionButtonComponent } from '../../atoms/action-button/action-button.component';
import { CancelLinkComponent } from '../../atoms/cancel-link/cancel-link.component';
import { DividerModule } from 'primeng/divider';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { getState } from '@ngrx/signals';
import { NgIf } from '@angular/common';
import { ComparisonValue } from '../../../models/geo-data';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';
import { districtKeyValidator } from '../../../validators/district-key.validator';
import { keyValidator } from '../../../validators/key.validator';

@Component({
  selector: 'app-comparison-value-form',
  standalone: true,
  imports: [
    PaginatorModule,
    ReactiveFormsModule,
    FormFieldWrapperComponent,
    InputTextModule,
    ActionButtonComponent,
    CancelLinkComponent,
    DividerModule,
    FormFooterComponent,
    NgIf,
  ],
  templateUrl: './comparison-value-form.component.html',
  styleUrl: './comparison-value-form.component.scss',
})
export class ComparisonValueFormComponent extends BaseFormComponent<ComparisonValue> {
  readonly store = inject(ComparisonValueStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: ComparisonValue;
  override formInitialized = false;
  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    effect(() => {
      const state = getState(this.store);
      if (state.selectedId) {
        this.initializeForm();
      }
    });
  }

  /**
   * Initializes a form with predefined values and validation rules.
   *
   * The method retrieves an item to edit using a store, sets up a reactive form group with form controls for 'name' and 'key',
   * and applies necessary validators. It also marks the form as initialized.
   *
   * @return {void} This method does not return a value.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();

    this.form = this.fb.group({
      name: [this.itemToEdit?.name || '', Validators.required],
      key: [
        this.itemToEdit?.key || '',
        keyValidator(this.store.keys(), this.itemToEdit?.key),
      ],
    });
    this.formInitialized = true;
  }

  /**
   * Handles the form submission action by invoking the submit method.
   *
   * @return {void} No return value; the form is submitted as a side effect.
   */
  onSubmit(): void {
    this.submit();
  }
}
